<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SignupForm from './components/Signup/Form.vue';
import AuthLayout from '../../../components/AuthLayout.vue';
import AuthHeading from '../../../components/AuthHeading.vue';
import AuthShowcase from '../../../components/AuthShowcase.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  PLANS,
  TRIAL_DAYS,
  billingFromQuery,
  planFromQuery,
} from 'shared/constants/talkrobinPlans';

const route = useRoute();
const { t } = useI18n();

// Chosen on the marketing site's pricing section: ?plan=pro&billing=yearly
const planId = computed(() => planFromQuery(route.query.plan));
const billing = computed(() => billingFromQuery(route.query.billing));
const plan = computed(() => PLANS[planId.value]);
const price = computed(() => plan.value.price[billing.value]);
const pricingUrl = computed(() => {
  const site = window.globalConfig?.BRAND_URL;
  return site ? `${site.replace(/\/$/, '')}/pricing` : '';
});

const description = computed(() =>
  plan.value.trial
    ? t('TALKROBIN_AUTH.SIGNUP.DESCRIPTION_TRIAL', {
        days: TRIAL_DAYS,
        plan: plan.value.name,
      })
    : t('TALKROBIN_AUTH.SIGNUP.DESCRIPTION_FREE')
);
</script>

<template>
  <AuthLayout>
    <template #header>
      {{ t('TALKROBIN_AUTH.SIGNUP.HAVE_ACCOUNT') }}
      <router-link
        to="/app/login"
        class="ml-1 font-semibold text-robin-indigo hover:text-robin-indigo-light dark:text-robin-indigo-light"
      >
        {{ t('TALKROBIN_AUTH.SIGNUP.LOG_IN') }}
      </router-link>
    </template>

    <AuthHeading
      :kicker="t('TALKROBIN_AUTH.SIGNUP.KICKER')"
      :title="t('TALKROBIN_AUTH.SIGNUP.TITLE')"
      :highlight="t('TALKROBIN_AUTH.SIGNUP.HIGHLIGHT')"
      :description="description"
    />
    <SignupForm :plan="planId" :billing="billing" />

    <template #aside>
      <AuthShowcase
        :title="t('TALKROBIN_AUTH.SIGNUP.SHOWCASE_TITLE')"
        :highlight="t('TALKROBIN_AUTH.SIGNUP.SHOWCASE_HIGHLIGHT')"
      >
        <div
          class="p-5 border rounded-2xl border-white/10 bg-white/[0.04] backdrop-blur"
        >
          <div class="flex items-start justify-between gap-4">
            <div>
              <p
                class="m-0 text-[11px] font-semibold tracking-[0.14em] uppercase text-white/50"
              >
                {{ t('TALKROBIN_AUTH.SIGNUP.SELECTED_PLAN') }}
              </p>
              <p class="m-0 mt-1 text-xl font-bold font-heading">
                {{ plan.name }}
                <span class="ml-1 text-sm font-medium font-inter text-white/60">
                  <template v-if="plan.trial">
                    {{
                      t('TALKROBIN_AUTH.SIGNUP.TRIAL_NOTE', {
                        days: TRIAL_DAYS,
                        price: `$${price}`,
                        period: t('TALKROBIN_AUTH.SIGNUP.PER_MONTH'),
                      })
                    }}
                    ·
                    {{
                      billing === 'yearly'
                        ? t('TALKROBIN_AUTH.SIGNUP.BILLED_YEARLY')
                        : t('TALKROBIN_AUTH.SIGNUP.BILLED_MONTHLY')
                    }}
                  </template>
                  <template v-else>
                    {{ t('TALKROBIN_AUTH.SIGNUP.FREE_FOREVER') }}
                  </template>
                </span>
              </p>
            </div>
            <a
              v-if="pricingUrl"
              :href="pricingUrl"
              class="text-sm font-semibold shrink-0 text-robin-indigo-light hover:text-white"
            >
              {{ t('TALKROBIN_AUTH.SIGNUP.CHANGE_PLAN') }}
            </a>
          </div>
          <ul
            class="flex flex-wrap m-0 mt-3 p-0 list-none gap-x-5 gap-y-1.5 text-sm text-white/80"
          >
            <li
              v-for="item in plan.highlights"
              :key="item"
              class="inline-flex items-center gap-2"
            >
              <Icon icon="i-lucide-check" class="size-4 text-robin-success" />
              {{ item }}
            </li>
          </ul>
        </div>
      </AuthShowcase>
    </template>
  </AuthLayout>
</template>
